/**
 * ToolTimelineItem - Individual tool call in the timeline.
 *
 * Displays:
 * - Status indicator (icon or progress ring)
 * - Tool name
 * - Duration badge
 * - Expandable arguments/results
 */

import { useState, useEffect } from 'react'
import {
  ChevronDown,
  ChevronRight,
  Wrench,
  CheckCircle,
  XCircle,
  Zap,
  Clock,
} from 'lucide-react'
import { ProgressRing } from './ProgressRing'
import { FeedbackButton } from '../FeedbackButton'
import type { ToolCall } from '@/store/chatStore'

interface ToolTimelineItemProps {
  toolCall: ToolCall
  isStreaming?: boolean
  showConnector?: boolean
}

export function ToolTimelineItem({
  toolCall,
  isStreaming: _isStreaming = false,
  showConnector = false,
}: ToolTimelineItemProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [elapsedMs, setElapsedMs] = useState(0)
  const [startTime] = useState(() => Date.now())

  // Track elapsed time for running tools
  useEffect(() => {
    if (toolCall.status !== 'running') return

    const interval = setInterval(() => {
      setElapsedMs(Date.now() - startTime)
    }, 100)

    return () => clearInterval(interval)
  }, [toolCall.status, startTime])

  const statusIcon = {
    pending: <ProgressRing status="pending" size={14} />,
    running: <ProgressRing status="running" size={14} />,
    success: <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />,
    error: <XCircle className="w-3.5 h-3.5 text-red-400" />,
  }

  const statusColors = {
    pending: 'border-amber-500/30 bg-amber-500/5',
    running: 'border-blue-500/30 bg-blue-500/5',
    success: 'border-emerald-500/20 bg-black/20',
    error: 'border-red-500/30 bg-red-500/5',
  }

  const connectorColors = {
    pending: 'bg-amber-500/30',
    running: 'bg-blue-500/30',
    success: 'bg-emerald-500/30',
    error: 'bg-red-500/30',
  }

  const displayDuration = toolCall.durationMs ?? (toolCall.status === 'running' ? elapsedMs : null)

  return (
    <div className="relative">
      {/* Timeline connector */}
      {showConnector && (
        <div
          className={`absolute left-[7px] -top-2 w-0.5 h-2 ${connectorColors[toolCall.status]}`}
        />
      )}

      <div
        className={`rounded-lg overflow-hidden border transition-all duration-300 slide-in-timeline ${statusColors[toolCall.status]}`}
      >
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/5 transition-colors"
        >
          {/* Status indicator */}
          <div className="flex-shrink-0 w-4 flex justify-center">
            {statusIcon[toolCall.status]}
          </div>

          {/* Chevron */}
          {isExpanded ? (
            <ChevronDown className="w-3 h-3 text-text-muted flex-shrink-0" />
          ) : (
            <ChevronRight className="w-3 h-3 text-text-muted flex-shrink-0" />
          )}

          {/* Tool icon and name */}
          <Wrench className="w-3 h-3 text-accent flex-shrink-0" />
          <span className="font-mono text-xs text-accent-hover truncate flex-1">
            {toolCall.name}
          </span>

          {/* Duration badge */}
          {displayDuration !== null && (
            <span className="flex items-center gap-1 text-[10px] text-text-muted bg-black/20 px-1.5 py-0.5 rounded">
              <Clock className="w-2.5 h-2.5" />
              {formatDuration(displayDuration)}
            </span>
          )}
        </button>

        {isExpanded && (
          <div className="px-3 pb-3 space-y-2 animate-in slide-in-from-top-1 duration-200">
            {/* Arguments */}
            {toolCall.arguments && Object.keys(toolCall.arguments).length > 0 && (
              <div>
                <div className="text-[10px] text-text-muted uppercase tracking-wide mb-1 flex items-center gap-1">
                  <Zap className="w-2.5 h-2.5" />
                  Arguments
                </div>
                <pre className="text-xs bg-black/30 rounded p-2 text-text-secondary whitespace-pre-wrap break-words">
                  {JSON.stringify(toolCall.arguments, null, 2)}
                </pre>
              </div>
            )}

            {/* Result */}
            {toolCall.result !== undefined && (
              <div>
                <div className="text-[10px] text-text-muted uppercase tracking-wide mb-1">
                  Result
                </div>
                <pre className="text-xs bg-black/30 rounded p-2 text-text-secondary whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
                  {typeof toolCall.result === 'string'
                    ? toolCall.result
                    : JSON.stringify(toolCall.result, null, 2)}
                </pre>
              </div>
            )}

            {/* Feedback button - show when tool is complete */}
            {(toolCall.status === 'success' || toolCall.status === 'error') && (
              <div className="pt-2 border-t border-white/5 flex items-center justify-between">
                <span className="text-[10px] text-text-muted">Was this helpful?</span>
                <FeedbackButton
                  auditLogId={toolCall.auditLogId}
                  toolName={toolCall.name}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

export default ToolTimelineItem
